import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import {
  useShop,
  useConsumerCart,
  useCartMatch,
  usePrevious,
  useModal,
  useCheckout
} from 'shop/hooks'
import { ProductList } from 'shop/components'
import { Categories } from './Categories'
import { includesCurrentCategory } from 'shop/components/Shop/Categories'

import {
  trackFBPixeltrackProductListViewed,
  trackGA4ProductListViewed
} from 'tracker'
import { CategoryWithProducts } from 'shop/components/Shop/Categories/types'
import { filterCatProductsBySearchTerm } from 'shop/components/Product/utils'
import { ASAP_ORDER, ShopBodyProps, SHOP_ORIGIN } from 'shop/types'
import { StickyCard } from './StickyCard'
import { useShopPage, useQueryParamsFulfillment } from 'shop/hooks'
import { PRODUCT_LIST } from './commonStyles'
import { getCurrentCartId, getInvalidItems } from '../CartModal/utils'
import { patchProducts } from 'shop/hooks/useGlobalContext/helpers'
import {
  AffectedProduct,
  CartErrorMessage,
  ConsumerCart,
  OrderItemWithLimit
} from 'shop/types/cart'
import CartProductErrorModal from '../Modal/CartProductErrorModal/CartProductErrorModal'

export const ShopBody = ({ slug: urlStoreSlug }: ShopBodyProps) => {
  const mounted = useRef(true)
  const {
    partner,
    setIsStoreLoading,
    categories,
    fetchCategories,
    config,
    isProductsLoading,
    setCategories,
    setIsProductsLoading
  } = useShop()

  const {
    handleAutoScrolling,
    isSearching,
    searchValue,
    setShouldShowAllCategories
  } = useShopPage()

  const { cart, cartLoading, affectedProducts, validateCart } =
    useConsumerCart()
  const { fulfillmentQueryParams } = useQueryParamsFulfillment()
  const { isModalOpen } = useModal()
  const { isCartMatch } = useCartMatch()
  const { productError, updateExceededLimitItems } = useCheckout()
  const [availableCategories, setAvailableCategories] = useState<
    CategoryWithProducts[] | null
  >(null)
  const [lastSelectedCategory, setLastSelectedCategory] = useState<string>('')
  const [selectedCategory, setSelectedCategory] = useState<string>('')

  const [shouldReloadProducts, setShouldReloadProducts] = useState(false)
  const [shouldCompareCartValues, setShouldCompareCartValues] = useState(false)

  const [showProductErrorModal, setShowProductErrorModal] = useState(false)
  const [productLimitReachedItems, setProductLimitReachedItems] = useState<
    OrderItemWithLimit[]
  >([])

  const { fulfillment, store, orderItems } = cart || {}
  const storeName = cart?.store.name
  const cartId = getCurrentCartId(config.domain)
  const fulfillmentModalActive = isModalOpen('fulfillment')

  /** Current cart values used in reload logic */
  const cartDependencies = useMemo(() => {
    return {
      fulfillmentFrom: fulfillment?.window.from,
      fulfillmentType: fulfillment?.type,
      orderType: fulfillment?.orderType,
      storeId: store?.id
    }
  }, [fulfillment, store?.id])

  /** Previous cart values used in reload logic */
  const oldCartValues = usePrevious(cartDependencies)

  /** Previous slug value used in reload logic */
  const oldStoreSlug = usePrevious(urlStoreSlug)

  /** Changing store slug should reload products */
  useEffect(() => {
    if (cartLoading || oldStoreSlug === urlStoreSlug) return
    setShouldReloadProducts(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartLoading, urlStoreSlug])

  useEffect(() => {
    // if we have opened the fulfillmentModal, ensure we compare the cart values even when it closes
    if (fulfillmentModalActive) setShouldCompareCartValues(true)
  }, [fulfillmentModalActive])

  /** Reload products when the cart changes via the fulfillment modal */
  useEffect(() => {
    if (
      shouldCompareCartValues &&
      (oldCartValues?.fulfillmentFrom !== cartDependencies.fulfillmentFrom ||
        oldCartValues?.fulfillmentType !== cartDependencies.fulfillmentType ||
        oldCartValues?.orderType !== cartDependencies.orderType ||
        oldCartValues?.storeId !== cartDependencies.storeId)
    ) {
      setShouldReloadProducts(true)
    }
    // if the cart changes whilst the fulfillmentModal is still open, do not reset the compare trigger
    // e.g. product change modal
    if (!fulfillmentModalActive) setShouldCompareCartValues(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartDependencies, oldCartValues])

  // Returning null to get default categories
  const getFulfillmentDatetime = useCallback(() => {
    if (!fulfillment) return null
    if (fulfillment.orderType === ASAP_ORDER) return null
    if (!isCartMatch && !fulfillmentModalActive) return null
    return fulfillment?.window?.from
  }, [fulfillment, isCartMatch, fulfillmentModalActive])

  const getCategoryVariables = useCallback(() => {
    // if isCartMatch use cart details / if fulfillmentModal is active use the cart values (cartMatch is false in this case before the url switches to the new url of the cart)
    const useCartData = isCartMatch || fulfillmentModalActive
    const fulfillmentType =
      (useCartData && fulfillment?.type) ||
      fulfillmentQueryParams.fulfillmentType

    const fulfillmentDateTime =
      (useCartData && getFulfillmentDatetime()) ||
      fulfillmentQueryParams.fulfillmentDatetime?.toISOString()

    // Only send fulfillmentDatetime if the cart is a match or fullfillment modal is active (user has picked a date in datepicker)
    let categoryVariables = {
      ...(useCartData && store?.id && { id: store.id }),
      slug: useCartData && store?.slug ? store.slug : urlStoreSlug,
      ...(fulfillmentDateTime && { fulfillmentDatetime: fulfillmentDateTime }),
      ...(fulfillmentType && { fulfillmentType })
    }

    return categoryVariables
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isCartMatch,
    fulfillmentModalActive,
    fulfillment,
    fulfillmentQueryParams.fulfillmentDatetime,
    store,
    urlStoreSlug,
    getFulfillmentDatetime
  ])

  useEffect(() => {
    // Load the categories with products data
    // cartIds are intialised when coming from the Landing page
    // and hence should not attempt to load any categories until the cart has intialised
    // if we already have category data but are updating the cart, exit.
    // otherwise force a reload if we want to through shouldReloadProducts.
    if (
      (cartLoading || (cartId && !cart) || categories.length) &&
      !shouldReloadProducts
    )
      return

    const categoryVariables = getCategoryVariables()
    fetchCategories(categoryVariables).then(({ categories }) => {
      // Reset availableCategories to avoid showing stale available categories after new categories are fetched
      setAvailableCategories(null)
      if (categories.length) {
        // If a discount is applied and affectedProducts is populated, patch affected product prices to products state when categories is fetched
        const patchedCategories = getPatchedCategories(
          categories,
          cart,
          affectedProducts
        )
        setCategories(patchedCategories)
      }
      setIsProductsLoading(false)
      setShouldReloadProducts(false)
      setLastSelectedCategory('')
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, cartLoading, shouldReloadProducts])

  /** Triggers when categories is already set, but an automatic discount has been applied as a result of cart value going beyond MOV threshold */
  useEffect(() => {
    if (!cart || !categories.length) return

    const patchedCategories = getPatchedCategories(
      categories,
      cart,
      affectedProducts
    )
    setCategories(patchedCategories)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affectedProducts])

  useEffect(() => {
    if (!orderItems?.length) return
    if (!productError) {
      setProductLimitReachedItems([])
      setShowProductErrorModal(false)
    }

    if (productError?.message === CartErrorMessage.PRODUCT_LIMIT_REACHED) {
      const items = getInvalidItems(
        productError,
        orderItems
      ) as OrderItemWithLimit[]
      if (!!items.length) {
        setProductLimitReachedItems(items)
        setShowProductErrorModal(true)
      } else {
        setProductLimitReachedItems([])
        setShowProductErrorModal(false)
      }
    }
  }, [productError, orderItems])

  /** Returns categories with discounted product prices or vanilla categories if no discount applied */
  const getPatchedCategories = (
    categories: CategoryWithProducts[],
    cart: ConsumerCart | null,
    affectedProducts: AffectedProduct[]
  ) => {
    const discountApplied = !!cart?.summary.discount?.code
    if (discountApplied && affectedProducts.length) {
      return patchProducts(categories, affectedProducts)
    }
    return categories
  }

  const track = useCallback(() => {
    if (categories.length > 0 && selectedCategory) {
      // legacy tracking
      trackFBPixeltrackProductListViewed()

      trackGA4ProductListViewed('slerpGA4Tracking')
      trackGA4ProductListViewed('merchantGA4Tracking')
    }
  }, [categories, selectedCategory])

  /** ProductList onLoad */
  useEffect(() => {
    setIsStoreLoading(true)
    track()

    window.scrollTo(0, 0)
    mounted.current = true

    return () => {
      mounted.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /** Once the Partner has loaded we can assume the useShop hook has loaded.
   * This is to keep functionality of a getMerchant call that has now been removed.
   */
  useEffect(() => {
    if (partner) {
      setIsStoreLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partner])

  /** Sets the initial category selected */
  useEffect(() => {
    if (!mounted.current) return
    if (!availableCategories?.length) return

    if (
      isSearching &&
      availableCategories.length > 0 &&
      !includesCurrentCategory(availableCategories, lastSelectedCategory) &&
      lastSelectedCategory !== ''
    ) {
      return setSelectedCategory(availableCategories[0].id)
    }
    if (lastSelectedCategory) return setSelectedCategory(lastSelectedCategory)
    // set the default cateogry to the first in the list that has an id
    const firstCatIndex = availableCategories.findIndex((cat) => cat.id)
    return setSelectedCategory(availableCategories[firstCatIndex].id)
  }, [lastSelectedCategory, availableCategories, isSearching])

  /** Filter Categories & Products further when searching */
  useEffect(() => {
    if (isProductsLoading) return
    if (searchValue && isSearching && categories.length) {
      const categoriesWithFiteredProducts = categories.map((cat) => {
        return {
          ...cat,
          products: filterCatProductsBySearchTerm(
            cat.name,
            cat.products,
            searchValue
          )
        }
      })

      const filteredCategories = categoriesWithFiteredProducts.filter((cat) => {
        return cat.products.length > 0
      })

      return setAvailableCategories(filteredCategories)
    }

    const filteredCategories = categories.filter((category) =>
      Boolean(category?.products?.length)
    )
    return setAvailableCategories(filteredCategories)
  }, [searchValue, isSearching, categories, isProductsLoading])

  /** Clear categories when leaving the page to avoid stale state on revisit */
  useEffect(() => {
    return () => {
      setCategories([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSetCategory = (categoryId: string) => {
    setLastSelectedCategory(categoryId)
    setSelectedCategory(categoryId)
  }

  const handleCategoryChange = (categoryId: string) => {
    // Show all categories permanently once user clicks on a category
    setShouldShowAllCategories(true)

    handleAutoScrolling()
    handleSetCategory(categoryId)

    // Use a small delay to ensure categories are rendered before scrolling
    setTimeout(() => {
      const element = document.getElementById(`product-category-${categoryId}`)

      if (!element) {
        return
      }

      const yOffset = 150
      const scrollPositon =
        element.getBoundingClientRect().top + window.pageYOffset - yOffset

      window.scrollTo({ top: scrollPositon, behavior: 'smooth' })
    }, 50)
  }

  const onProductLimitReachedContinue = async () => {
    const limitedItems = productLimitReachedItems as OrderItemWithLimit[]
    await updateExceededLimitItems(limitedItems)
    setShowProductErrorModal(false)
    validateCart(false)
  }

  return (
    <>
      {productError && showProductErrorModal && storeName && (
        <CartProductErrorModal
          loading={cartLoading}
          invalidItems={productLimitReachedItems}
          storeName={storeName}
          onContinue={onProductLimitReachedContinue}
          origin={SHOP_ORIGIN}
          productError={productError}
          backButtonEnabled={false}
          modalProps={{
            inModal: false,
            isOverlay: true,
            isOpen: false,
            zIndex: 200
          }}
        />
      )}
      <StickyCard
        id='shop-body-root'
        hasHeaderOffset
        type={PRODUCT_LIST}
        header={
          <Categories
            selectedCategory={selectedCategory}
            onClick={handleCategoryChange}
            categories={availableCategories}
          />
        }
        content={
          <ProductList
            setActiveCategory={handleSetCategory}
            selectedCategory={selectedCategory}
            categories={availableCategories}
          />
        }
      />
    </>
  )
}
